import { z } from 'zod';

/**
 * Common field mapping type schema
 */
export const fieldMappingTypeSchema = z.enum([
  'constant',
  'key',
  'split',
  'concat',
]);

/**
 * FTP protocol type schema
 */
export const ftpProtocolTypeSchema = z.enum(['ftp', 'sftp']);

/**
 * Entity type schema
 */
export const entityTypeSchema = z.enum([
  'site',
  'permission_grant',
  'accounting_reference',
  'user',
  'organizational_unit',
]);

/**
 * Occurrence schema - job frequency in fractions of a day
 */
export const occurenceSchema = z
  .union([z.literal(0.04), z.literal(0.125), z.literal(0.5), z.literal(1)])
  .describe(
    'Occurrence of the job in fractions of a day. A single day (24 hours) equals 1',
  );

/**
 * Mapping entry schema with optional fields for split type
 */
export const mappingEntryObjectSchema = z.object({
  index: z.coerce.number().optional().describe('Used only for split type'),
  replace: z.boolean().optional().default(false),
  separator: z
    .string()
    .optional()
    .describe('Used only for split and concat type'),
  type: fieldMappingTypeSchema,
  value: z.union([z.string(), z.number(), z.boolean()]),
  values: z.array(z.string()).optional().describe('Used only for concat type'),
});

/**
 * Required mapping entry with validation for value/values based on type
 */
export const requiredMappingEntryObjectSchema = mappingEntryObjectSchema
  .refine(
    (data) => data.type === 'concat' || (data.value && data.value.length >= 1),
    { path: ['value'] },
  )
  .refine(
    (data) =>
      data.type !== 'concat' || (data.values && data.values.length >= 1),
    { path: ['values'] },
  );

type MappingEntry = z.output<typeof mappingEntryObjectSchema>;

function normalizeMappingEntry(
  data: z.input<typeof mappingEntryObjectSchema>,
): MappingEntry {
  return {
    ...data,
    index: typeof data.index === 'number' ? data.index : undefined,
    replace: data.replace ?? false,
  };
}

/**
 * Optional mapping entry that is stripped entirely when value is an empty string
 */
export const optionalMappingEntrySchema = z.codec(
  mappingEntryObjectSchema.optional(),
  mappingEntryObjectSchema.optional(),
  {
    // UI → API: strip entry if value is empty
    decode(data) {
      if (data === undefined || data.value === '') return undefined;

      return normalizeMappingEntry(data);
    },
    // API → UI: pass through
    encode(data) {
      if (data === undefined) return undefined;

      return normalizeMappingEntry(data);
    },
  },
);

// API format: record/object
const valueMappingAPISchema = z.record(z.string(), z.string());

/**
 * Value mapping schemas for UI and API formats
 */
// UI format: array of key-value pairs
const valueMappingUISchema = z.array(valueMappingAPISchema);

/**
 * Codec for valueMapping: UI format (array of records) ↔ API format (single record)
 */
export const valueMappingCodec = z.codec(
  valueMappingUISchema,
  valueMappingAPISchema,
  {
    // UI → API: Merge array of records into single record
    decode(arrayInput) {
      const merged: Record<string, string> = {};

      for (const record of arrayInput) {
        Object.assign(merged, record);
      }

      return merged;
    },
    // API → UI: Convert single record to array of single-entry records
    encode(recordInput) {
      return Object.entries(recordInput).map(([key, value]) => ({
        [key]: value,
      }));
    },
  },
);

export const valueMappingSchema = valueMappingAPISchema;
